/* eslint-disable jsx-a11y/media-has-caption */

import React, { useState } from "react";

import { makeDelete } from "@culturehq/client";
import { Icon, PlainButton, Thumbnail } from "@culturehq/components";
import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import { canAdminSocialShare } from "app/permissions";
import { getImageFromStory, t, Trans } from "app/utils";

import linkedin from "images/social/linkedin.svg";
import like from "images/social/linkedin/like_selected.svg";

import imagesSizes from "constants/imagesSizes";

import Subtitles from "components/common/Subtitles";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";
import LinkedinReactionIcon from "components/linkedin/LinkedinReactionIcon";
import LinkedinReactionText from "components/linkedin/LinkedinReactionText";
import LinkedinReaction from "components/linkedin/LinkedinReaction";
import StoryShareLinkedin from "components/linkedin/StoryShareLinkedin";
import EngageLinkedin from "components/linkedin/EngageLinkedin";
import QuickOptions from "components/display/QuickOptions";

import LinkedinMentionText from "components/linkedin/LinkedinMentionText";
import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import PinUserActivityToggle from "../PinUserActivityToggle";

const PostQuickOptions = ({
  userActivity, onUserActivityUpdate, onUserActivityRemove
}) => {
  const { profile } = useProfile();
  const { entity: socialShare } = userActivity;

  const onSocialShareDeleted = async () => {
    await makeDelete(`/stories/social_share/${socialShare.id}`);
    onUserActivityRemove(userActivity.id);
  };

  return (
    <QuickOptions>
      <ul className="dropdown-menu">
        {profile.organizationAdmin && (
          <li>
            <PinUserActivityToggle
              userActivity={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
            />
          </li>
        )}
        {canAdminSocialShare(profile, socialShare) && (
          <li>
            <ChqModalConfirmDelete
              onAccept={onSocialShareDeleted}
              trigger={onTrigger => (
                <PlainButton onClick={onTrigger} className="link action">
                  <Icon icon="trash-a" />
                  <Trans>Delete</Trans>
                </PlainButton>
              )}
            >
              <Trans>Are you sure you want to delete this social share?</Trans>
            </ChqModalConfirmDelete>
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

const LinkedInCreatorInfo = ({ story }) => {
  const { creator, socialShares } = story;
  const socialShare = socialShares ? socialShares[0] : undefined;

  if (socialShare?.socialCompany) {
    return (
      <>
        <span className="Linkedin_Creator--Information_Creator">{socialShare.socialCompany.name}</span>
        <Trans>&apos;s story on LinkedIn!</Trans>
      </>
    );
  }

  if (socialShare?.creator?.id === creator.id) {
    return (
      <Trans>its story on LinkedIn!</Trans>
    );
  }

  return (
    <>
      <ProfileLink user={creator} />
      <Trans>&apos;s story on LinkedIn!</Trans>
    </>
  );
};

const LinkedinPostPreview = ({
  linkedinShare,
  onUserActivityRemove,
  onUserActivityUpdate,
  story,
  updateLinkedinShare,
  userActivity
}) => {
  const [imageUrl] = useState(story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail);

  const getAccountImage = () => {
    if (linkedinShare.socialCompany) {
      return linkedinShare.socialCompany.logo;
    }
    return linkedinShare?.creator?.avatar?.thumbUrl || story.creator.avatar.thumbUrl;
  };

  const getAccountText = () => {
    if (linkedinShare.socialCompany) {
      return linkedinShare.socialCompany.name;
    }
    return linkedinShare.creator.name;
  };

  return (
    <div className="FeedItem__preview__photo_created Story FeedItem__preview__social">
      <FeedItemTitle
        item={userActivity}
        date={userActivity.bumpedAt}
        user={linkedinShare?.creator || story.creator}
      >
        <ProfileLink user={linkedinShare?.creator || story.creator} />
        {linkedinShare?.creator ? (
          <span>
            {" "}
            <Trans>shared</Trans>
            {" "}
            <LinkedInCreatorInfo story={story} />
          </span>
        ) : (
          <span>
            <Trans>&apos;s story has been approved to share on social!</Trans>
          </span>
        )}
        <PostQuickOptions
          userActivity={userActivity}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />
      </FeedItemTitle>
      <EngageLinkedin
        story={story}
        linkedinShare={linkedinShare}
        updateLinkedinShare={updateLinkedinShare}
        displayModalFromUrl
      >
        <div className="FeedItem__preview__Social__Container FeedItem__preview__Linkedin__Container">
          <div className="FeedItem__preview__Social__Header">
            <div className="FeedItem__preview__Social__User">
              <div className="User__Info-Container">
                <Thumbnail className="Thumbnail" image={getAccountImage()} title={linkedinShare?.creator?.name} />
                <div>
                  <p className="Preview-User__Info Preview-User__Info-Name">{getAccountText()}</p>
                </div>
              </div>
              <img className="social-icon" src={linkedin} alt="LinkedIn" />
            </div>
            <p className="FeedItem__preview__Social__Message"><LinkedinMentionText value={linkedinShare.message} /></p>
          </div>
          <div className="gallery-lightbox__photo">
            <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }} />
            {story.media.mediaType === "image" && (
              <img
                className="gallery-lightbox__photo--image"
                src={imageUrl}
                alt={story.imageAlt || story.question.question}
              />
            )}
            {story.visuals.length > 1 && (
              <div className="MultiImage__container">
                <Icon icon="images" />
              </div>
            )}
            {story.media.mediaType === "video" && (
              <>
                <video
                  controls={false}
                  className="gallery-lightbox__main-image gallery-lightbox__main-video"
                  poster={`${story.media.thumbnail}?not-from-cache-please`}
                  src={story.media.url}
                  disablePictureInPicture
                  crossOrigin="anonymous"
                  controlsList="nodownload"
                >
                  <Subtitles media={story.media} />
                </video>
              </>
            )}
          </div>
          <div className="FeedItem__preview__Social__Footer">
            {(linkedinShare.reactions > 0 || linkedinShare.comments > 0) && (
              <>
                <div className="Interactions-Container">
                  <div className="Interactions-Container__Reactions">
                    {linkedinShare.reactions > 0 && <img className="Linkedin-Reaction" src={like} alt="Like" />}
                    <span className="Reactions__Count">{linkedinShare.reactions}</span>
                  </div>
                  <span className="Comments__Count">{`${linkedinShare.comments} comment${linkedinShare.comments === 1 ? "" : "s"}`}</span>
                </div>
                <hr />
              </>
            )}
            <div className="Linkedin-Actions">
              <LinkedinReaction disabled>
                <LinkedinReactionIcon />
                <LinkedinReactionText />
              </LinkedinReaction>
              <PlainButton>
                <Icon icon="linkedin-comment" />
                <Trans>Comment</Trans>
              </PlainButton>
              <StoryShareLinkedin
                story={story}
                linkedinShare={linkedinShare}
                placeholder
              />
            </div>
          </div>
        </div>
      </EngageLinkedin>
    </div>
  );
};

export default LinkedinPostPreview;
